/**
 * Observability Identity Hook
 *
 * Bridges authentication state to Datadog RUM and Logs SDKs.
 * Sets user context (id, name, email, role, company) when authenticated.
 * Fetches /profile/me to resolve the company name from the backend.
 * Mount this hook once in App.tsx to ensure all sessions are identified.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { apiClient } from '@/core/api/client';
import { setRumUser, clearRumUser, setRumAccount, clearRumAccount } from './rum';
import { setLogsUser, clearLogsUser } from './datadogLogs';

type ProfileMeResponse = {
  companyName: string | null;
  partnerCompanyId?: string | null;
};

const ROLE_LABELS: Record<string, string> = {
  CARE_SEEKER: 'Care Seeker',
  FINANCIAL_ADVISOR: 'Financial Advisor',
  SERVICE_PROVIDER: 'Service Provider',
  ADMIN: 'Admin',
};

type CompanyInfo = {
  companyName: string | null;
  partnerCompanyId: string | null;
};

const fetchCompanyName = async (): Promise<CompanyInfo | null> => {
  try {
    const { data } = await apiClient.get<ProfileMeResponse>('/users/profile/me');
    return {
      companyName: data.companyName ?? null,
      partnerCompanyId: data.partnerCompanyId ?? null,
    };
  } catch {
    return null;
  }
};

/**
 * Syncs the authenticated user identity to Datadog RUM and Logs.
 * Automatically sets user context on login and clears it on logout.
 * Enriches identity with company name fetched from the backend.
 */
export const useObservabilityIdentity = (): void => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const currentUserId = user?.id ?? null;

    if (currentUserId === previousUserIdRef.current) {
      return;
    }

    previousUserIdRef.current = currentUserId;

    if (!isAuthenticated || !user) {
      clearRumUser();
      clearRumAccount();
      clearLogsUser();
      return;
    }

    // Set basic identity immediately (role is already on the auth user)
    const baseIdentity = {
      id: user.id,
      name: [user.firstName, user.lastName].filter(Boolean).join(' '),
      email: user.email,
      role: ROLE_LABELS[user.role] ?? user.role,
    };

    setRumUser(baseIdentity);
    setLogsUser(baseIdentity);

    fetchCompanyName()
      .then((profile) => {
        if (!profile) return;
        if (previousUserIdRef.current !== currentUserId) return;

        const { companyName, partnerCompanyId } = profile;

        if (companyName) {
          const enrichedIdentity = { ...baseIdentity, company: companyName };
          setRumUser(enrichedIdentity);
          setLogsUser(enrichedIdentity);
        }

        const accountId = partnerCompanyId ?? companyName;
        if (accountId) {
          setRumAccount({
            id: accountId,
            ...(companyName && { name: companyName }),
          });
        }
      })
      .catch(() => {});
  }, [user, isAuthenticated, isLoading]);
};
